<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

/**
 * Selector custom reutilizable para las landings.
 * Abre siempre hacia abajo, con altura máxima y scroll interno.
 * Soporta opciones deshabilitadas.
 */
const props = withDefaults(
  defineProps<{
    modelValue: any;
    options: any[];
    placeholder?: string;
    labelKey?: string;
    valueKey?: string;
    disabledKey?: string;
    maxHeight?: number;
    disabled?: boolean;
  }>(),
  {
    placeholder: 'Elige una opción',
    labelKey: 'label',
    valueKey: 'value',
    disabledKey: 'disabled',
    maxHeight: 260,
    disabled: false,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void;
  (e: 'change', value: any): void;
}>();

const rootRef = ref<HTMLElement | null>(null);
const open = ref<boolean>(false);

const selected = computed(() =>
  props.options.find((o: any) => o[props.valueKey] === props.modelValue),
);
const isDisabled = (o: any) => !!o[props.disabledKey];

const toggle = () => {
  if (props.disabled) return;
  open.value = !open.value;
};

const choose = (o: any) => {
  if (isDisabled(o)) return;
  emit('update:modelValue', o[props.valueKey]);
  emit('change', o[props.valueKey]);
  open.value = false;
};

const onDocClick = (e: MouseEvent) => {
  if (rootRef.value && !rootRef.value.contains(e.target as Node)) {
    open.value = false;
  }
};

onMounted(() => document.addEventListener('click', onDocClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocClick));
</script>

<template>
  <div ref="rootRef" class="c-select" :class="{ open, disabled }">
    <button
      type="button"
      class="c-select__toggle"
      :disabled="disabled"
      @click.stop="toggle"
    >
      <span :class="{ 'c-select__placeholder': !selected }">
        {{ selected ? selected[labelKey] : placeholder }}
      </span>
      <span class="c-select__caret" aria-hidden="true">▾</span>
    </button>
    <ul
      v-if="open"
      class="c-select__menu"
      :style="{ maxHeight: maxHeight + 'px' }"
    >
      <li
        v-for="(o, i) in options"
        :key="i"
        class="c-select__option"
        :class="{
          selected: o[valueKey] === modelValue,
          disabled: isDisabled(o),
        }"
        @click="choose(o)"
      >
        {{ o[labelKey] }}
      </li>
    </ul>
  </div>
</template>

<style scoped>
.c-select {
  position: relative;
  font-family: 'Poppins', system-ui, sans-serif;
}
.c-select__toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border: 1.5px solid #e6e6e6;
  border-radius: 8px;
  background: #ffffff;
  color: #3a3a3a;
  font-size: 16px;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.c-select.open .c-select__toggle,
.c-select__toggle:focus {
  outline: none;
  border-color: #5a9dbf;
  box-shadow: 0 0 0 3px rgba(90, 157, 191, 0.2);
}
.c-select__placeholder {
  color: #888888;
}
.c-select__caret {
  color: #888888;
  font-size: 12px;
  flex-shrink: 0;
  transition: transform 0.15s;
}
.c-select.open .c-select__caret {
  transform: rotate(180deg);
}
.c-select__menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 30;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #ffffff;
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow-y: auto;
}
.c-select__option {
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 15px;
  color: #3a3a3a;
  cursor: pointer;
}
.c-select__option:hover {
  background: #f7f7f7;
}
.c-select__option.selected {
  background: rgba(90, 157, 191, 0.12);
  color: #4a88a8;
  font-weight: 600;
}
.c-select__option.disabled {
  color: #b0b0b0;
  cursor: not-allowed;
}
.c-select__option.disabled:hover {
  background: transparent;
}
.c-select.disabled .c-select__toggle {
  background: #f7f7f7;
  color: #888888;
  cursor: not-allowed;
}
</style>
